import type { BitmapData } from "./bitmapdata";
import { EventDispatcher } from "./events";
import type { BitmapFilter } from "./filters";
import { ColorTransform, Matrix, Rectangle } from "./geom";

/**
 * flash.display: the display list, walked into a 2d context once per visual
 * frame.
 *
 * `$`-prefixed members are the shim's internal render protocol ($render,
 * $draw, $localBounds, $bounds, $matrix, $concatenatedMatrix, $colorTransform,
 * $filters, $frame). They are not flash API; the prefix keeps them clear of the
 * ported AS3 subclasses, which declare plain names like `frame` or `filters` of
 * their own. Rectangles returned by $localBounds/$bounds are read-only and may
 * be reused by the next call, so callers copy before retaining one.
 */

/**
 * Walk state, mutated in place as the walk descends and restored on the way
 * out, so a frame costs no per-object allocation.
 */
export interface RenderState {
  ctx: CanvasRenderingContext2D;
  alpha: number;
  colorTransform: ColorTransform | null;
}

export class Transform {
  constructor(private readonly owner: DisplayObject) {}

  get colorTransform(): ColorTransform {
    return this.owner.$colorTransform?.clone() ?? new ColorTransform();
  }

  set colorTransform(value: ColorTransform) {
    this.owner.$colorTransform = value.isIdentity ? null : value.clone();
  }
}

export abstract class DisplayObject extends EventDispatcher {
  x = 0;
  y = 0;
  scaleX = 1;
  scaleY = 1;
  /** Degrees, matching flash. */
  rotation = 0;
  alpha = 1;
  visible = true;
  parent: DisplayObjectContainer | null = null;
  /** Flash blend mode name; only "add" is mapped to a canvas operator. */
  blendMode = "normal";
  /** Pure performance hint in flash; retained so ported code can set it. */
  cacheAsBitmap = false;
  /**
   * Clip this object with another. Flash also stops drawing the mask in its own
   * right the moment it is assigned, which is what `$maskTarget` records.
   */
  get mask(): DisplayObject | null {
    return this.$mask;
  }

  set mask(value: DisplayObject | null) {
    if (this.$mask === value) return;
    if (this.$mask) this.$mask.$maskTarget = null;
    this.$mask = value;
    if (value) value.$maskTarget = this;
  }

  /** The object this one masks, if any. Set by the `mask` setter. */
  $maskTarget: DisplayObject | null = null;
  private $mask: DisplayObject | null = null;

  $colorTransform: ColorTransform | null = null;
  $filters: BitmapFilter[] = [];

  get transform(): Transform {
    return (this.transformObject ??= new Transform(this));
  }

  get filters(): BitmapFilter[] {
    return this.$filters.slice();
  }

  set filters(value: BitmapFilter[] | null) {
    this.$filters = value ? value.slice() : [];
  }

  get stage(): Stage | null {
    return stageInstance;
  }

  get width(): number {
    return this.$bounds(sizeBounds)?.width ?? 0;
  }

  set width(value: number) {
    const local = this.$localBounds();
    if (!local || local.width === 0) return;
    this.scaleX = value / local.width;
  }

  get height(): number {
    return this.$bounds(sizeBounds)?.height ?? 0;
  }

  set height(value: number) {
    const local = this.$localBounds();
    if (!local || local.height === 0) return;
    this.scaleY = value / local.height;
  }

  /** Bounds in this object's own coordinate space. */
  abstract $localBounds(): Rectangle | null;

  /** This object's own transform, written into `out`. */
  $matrix(out: Matrix = new Matrix()): Matrix {
    const radians = (this.rotation * Math.PI) / 180;
    const cos = Math.cos(radians);
    const sin = Math.sin(radians);
    out.a = cos * this.scaleX;
    out.b = sin * this.scaleX;
    out.c = -sin * this.scaleY;
    out.d = cos * this.scaleY;
    out.tx = this.x;
    out.ty = this.y;
    return out;
  }

  $concatenatedMatrix(out: Matrix = new Matrix()): Matrix {
    this.$matrix(out);
    let node = this.parent;
    while (node) {
      out.concat(node.$matrix(ancestorMatrix));
      node = node.parent;
    }
    return out;
  }
  /** Whether a stage-space point falls inside this object's visible local bounds. */
  hitTestPoint(stageX: number, stageY: number): boolean {
    let node: DisplayObject | null = this;
    while (node) {
      if (!node.visible || node.alpha <= 0 || node.$maskTarget) return false;
      node = node.parent;
    }
    const local = this.$localBounds();
    if (!local) return false;
    const inverse = this.$concatenatedMatrix(hitMatrix);
    if (inverse.a * inverse.d - inverse.b * inverse.c === 0) return false;
    inverse.invert();
    const x = inverse.a * stageX + inverse.c * stageY + inverse.tx;
    const y = inverse.b * stageX + inverse.d * stageY + inverse.ty;
    return (
      x >= local.x && x <= local.right && y >= local.y && y <= local.bottom
    );
  }

  /** Bounds in the parent's coordinate space. */
  $bounds(out: Rectangle = new Rectangle()): Rectangle | null {
    const local = this.$localBounds();
    if (!local) return null;
    return transformBounds(local, this.$matrix(boundsMatrix), out);
  }

  /** Entry point used by parents: applies transform, filters, mask, blend. */
  $render(state: RenderState): void {
    if (!this.visible || this.alpha <= 0 || this.$maskTarget) return;
    const alpha = state.alpha * this.alpha;
    if (alpha <= 0) return;

    const parentColorTransform = state.colorTransform;
    let colorTransform = parentColorTransform;
    if (this.$colorTransform) {
      colorTransform = parentColorTransform
        ? concatInto(
            parentColorTransform,
            this.$colorTransform,
            (this.mergedColorTransform ??= new ColorTransform()),
          )
        : this.$colorTransform;
    }

    if (this.$filters.length > 0 || this.mask) {
      this.renderLayer(state, alpha, colorTransform);
      return;
    }

    const ctx = state.ctx;
    ctx.save();
    this.applyTransform(ctx);
    this.applyBlendMode(ctx);
    const parentAlpha = state.alpha;
    state.alpha = alpha;
    state.colorTransform = colorTransform;
    this.$draw(state);
    state.alpha = parentAlpha;
    state.colorTransform = parentColorTransform;
    ctx.restore();
  }

  /** Draws this object's own content in its local space. */
  protected abstract $draw(state: RenderState): void;

  private applyTransform(ctx: CanvasRenderingContext2D): void {
    if (this.rotation === 0) {
      ctx.transform(this.scaleX, 0, 0, this.scaleY, this.x, this.y);
      return;
    }
    const radians = (this.rotation * Math.PI) / 180;
    const cos = Math.cos(radians);
    const sin = Math.sin(radians);
    ctx.transform(
      cos * this.scaleX,
      sin * this.scaleX,
      -sin * this.scaleY,
      cos * this.scaleY,
      this.x,
      this.y,
    );
  }

  private applyBlendMode(ctx: CanvasRenderingContext2D): void {
    // only "add" is mapped; CutScene's one "darken" sprite has always drawn
    // normally here, so leaving the rest unmapped keeps the current output.
    if (this.blendMode === "add") ctx.globalCompositeOperation = "lighter";
  }

  private renderLayer(
    state: RenderState,
    alpha: number,
    colorTransform: ColorTransform | null,
  ): void {
    // Filters and masks operate on the object's rasterised local-space bitmap,
    // then that bitmap is placed with the object's own matrix, so blur radii
    // scale with the object exactly as they do in flash.
    const local = this.$localBounds();
    if (!local || local.width <= 0 || local.height <= 0) return;
    let pad = 0;
    for (const filter of this.$filters) pad = Math.max(pad, filter.padding);
    const bx = Math.floor(local.x) - pad;
    const by = Math.floor(local.y) - pad;
    const bw = Math.ceil(local.width) + pad * 2;
    const bh = Math.ceil(local.height) + pad * 2;
    if (bw <= 0 || bh <= 0 || bw > 4096 || bh > 4096) return;

    const ctx = state.ctx;
    const parentAlpha = state.alpha;
    const parentColorTransform = state.colorTransform;
    const layer = takeScratch(bw, bh);
    layer.ctx.translate(-bx, -by);
    state.ctx = layer.ctx;
    state.alpha = 1;
    state.colorTransform = colorTransform;
    this.$draw(state);

    if (this.mask) {
      const maskLayer = takeScratch(bw, bh);
      // The mask lives in its own branch of the tree (usually a sibling), so
      // its matrix must be rebased into this object's local space -- otherwise
      // a scaled target (the xenos bar) drags the mask along with it and the
      // alpha window stops clipping anything.
      const rebased = this.mask.$concatenatedMatrix(maskMatrix);
      const inverseSelf = this.$concatenatedMatrix(maskInverse);
      inverseSelf.invert();
      rebased.concat(inverseSelf);
      maskLayer.ctx.translate(-bx, -by);
      maskLayer.ctx.transform(
        rebased.a,
        rebased.b,
        rebased.c,
        rebased.d,
        rebased.tx,
        rebased.ty,
      );
      state.ctx = maskLayer.ctx;
      state.colorTransform = null;
      this.mask.$draw(state);
      layer.ctx.setTransform(1, 0, 0, 1, 0, 0);
      layer.ctx.globalCompositeOperation = "destination-in";
      layer.ctx.drawImage(maskLayer.canvas, 0, 0, bw, bh, 0, 0, bw, bh);
      layer.ctx.globalCompositeOperation = "source-over";
      releaseScratch(maskLayer);
    }

    state.ctx = ctx;
    state.alpha = parentAlpha;
    state.colorTransform = parentColorTransform;

    ctx.save();
    this.applyTransform(ctx);
    ctx.globalAlpha = alpha;
    this.applyBlendMode(ctx);
    if (this.$filters.length > 0) ctx.filter = cssFilters(this.$filters);
    ctx.drawImage(layer.canvas, 0, 0, bw, bh, bx, by, bw, bh);
    ctx.restore();
    releaseScratch(layer);
  }

  private transformObject: Transform | null = null;
  /** Reused when this object's own tint has to be concatenated with a parent's. */
  private mergedColorTransform: ColorTransform | null = null;
}

export class DisplayObjectContainer extends DisplayObject {
  get numChildren(): number {
    return this.children.length;
  }

  addChild<T extends DisplayObject>(child: T): T {
    if (child.parent === this) {
      const i = this.children.indexOf(child);
      if (i >= 0) this.children.splice(i, 1);
    } else {
      child.parent?.removeChild(child);
    }
    this.children.push(child);
    child.parent = this;
    return child;
  }

  removeChild<T extends DisplayObject>(child: T): T {
    const i = this.children.indexOf(child);
    if (i >= 0) {
      this.children.splice(i, 1);
      child.parent = null;
    }
    return child;
  }

  removeChildAt(index: number): DisplayObject | null {
    const child = this.children[index];
    if (!child) return null;
    this.children.splice(index, 1);
    child.parent = null;
    return child;
  }

  getChildAt(index: number): DisplayObject | null {
    return this.children[index] ?? null;
  }

  $localBounds(): Rectangle | null {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    const childBounds = new Rectangle();
    for (const child of this.children) {
      if (!child.visible) continue;
      const b = child.$bounds(childBounds);
      if (!b) continue;
      if (b.x < minX) minX = b.x;
      if (b.y < minY) minY = b.y;
      if (b.right > maxX) maxX = b.right;
      if (b.bottom > maxY) maxY = b.bottom;
    }
    if (minX === Infinity) return null;
    return new Rectangle(minX, minY, maxX - minX, maxY - minY);
  }

  protected $draw(state: RenderState): void {
    const children = this.children;
    for (let i = 0; i < children.length; i++) children[i].$render(state);
  }

  private readonly children: DisplayObject[] = [];
}

/** Only the beginBitmapFill/drawRect/endFill subset the game uses. */
export class Graphics {
  beginBitmapFill(
    bitmap: BitmapData,
    matrix: Matrix | null = null,
    repeat = true,
    smooth = false,
  ): void {
    this.currentFill = {
      bitmap,
      repeat,
      smooth,
      patternMatrix: matrix
        ? new DOMMatrix([
            matrix.a,
            matrix.b,
            matrix.c,
            matrix.d,
            matrix.tx,
            matrix.ty,
          ])
        : null,
      pattern: null,
      patternCtx: null,
      patternVersion: -1,
    };
  }

  drawRect(x: number, y: number, width: number, height: number): void {
    this.commands.push({
      fill: this.currentFill,
      rect: new Rectangle(x, y, width, height),
    });
    if (!this.bounds) {
      this.bounds = new Rectangle(x, y, width, height);
      return;
    }
    const minX = Math.min(this.bounds.x, x);
    const minY = Math.min(this.bounds.y, y);
    const maxX = Math.max(this.bounds.right, x + width);
    const maxY = Math.max(this.bounds.bottom, y + height);
    this.bounds.setTo(minX, minY, maxX - minX, maxY - minY);
  }

  endFill(): void {
    this.currentFill = null;
  }

  /** Extent of everything drawn so far, or null while empty. Read-only. */
  $bounds(): Rectangle | null {
    return this.bounds;
  }

  $draw(ctx: CanvasRenderingContext2D, alpha: number): void {
    if (this.commands.length === 0) return;
    ctx.globalAlpha = alpha;
    for (const command of this.commands) {
      const fill = command.fill;
      if (!fill) continue;
      const pattern = this.patternFor(ctx, fill);
      if (!pattern) continue;
      const previousSmoothing = ctx.imageSmoothingEnabled;
      ctx.imageSmoothingEnabled = fill.smooth;
      ctx.fillStyle = pattern;
      ctx.fillRect(
        command.rect.x,
        command.rect.y,
        command.rect.width,
        command.rect.height,
      );
      ctx.imageSmoothingEnabled = previousSmoothing;
    }
  }

  /**
   * Patterns are per-context objects, so one is cached per fill and rebuilt
   * only when the fill is drawn into a different context (a filter or mask
   * layer) or its bitmap has been written to since.
   */
  private patternFor(
    ctx: CanvasRenderingContext2D,
    fill: BitmapFill,
  ): CanvasPattern | null {
    if (
      fill.pattern &&
      fill.patternCtx === ctx &&
      fill.patternVersion === fill.bitmap.version
    ) {
      return fill.pattern;
    }
    const pattern = ctx.createPattern(
      fill.bitmap.source,
      fill.repeat ? "repeat" : "no-repeat",
    );
    if (!pattern) return null;
    if (fill.patternMatrix) pattern.setTransform(fill.patternMatrix);
    fill.pattern = pattern;
    fill.patternCtx = ctx;
    fill.patternVersion = fill.bitmap.version;
    return pattern;
  }

  private readonly commands: DrawCommand[] = [];
  private currentFill: BitmapFill | null = null;
  private bounds: Rectangle | null = null;
}

export class Sprite extends DisplayObjectContainer {
  readonly graphics = new Graphics();

  $localBounds(): Rectangle | null {
    const childBounds = super.$localBounds();
    const g = this.graphics.$bounds();
    if (!g) return childBounds;
    if (!childBounds) return g;
    const minX = Math.min(childBounds.x, g.x);
    const minY = Math.min(childBounds.y, g.y);
    const maxX = Math.max(childBounds.right, g.right);
    const maxY = Math.max(childBounds.bottom, g.bottom);
    return new Rectangle(minX, minY, maxX - minX, maxY - minY);
  }

  protected $draw(state: RenderState): void {
    this.graphics.$draw(state.ctx, state.alpha);
    super.$draw(state);
  }
}

/**
 * MovieClip with a frame counter. The port only needs the main-timeline frame
 * addressing Game uses, so frames carry no per-frame display list and nothing
 * drives playback on its own.
 */
export class MovieClip extends Sprite {
  totalFrames = 1;
  frameScripts: Record<number, () => void> = {};

  get currentFrame(): number {
    return this.$frame;
  }

  gotoAndStop(frame: number | string): void {
    this.$frame = typeof frame === "number" ? frame : 1;
    this.frameScripts[this.$frame]?.();
  }

  // no timeline driver here, so there is no playback state to clear; Game's
  // constructor still calls stop() the way the AS3 did.
  stop(): void {}

  // $-prefixed because AS3 subclasses keep their own `frame` field.
  private $frame = 1;
}

export class Bitmap extends DisplayObject {
  smoothing = false;

  constructor(public bitmapData: BitmapData | null = null) {
    super();
  }

  $localBounds(): Rectangle | null {
    const bd = this.bitmapData;
    if (!bd) return null;
    this.localBounds.width = bd.width;
    this.localBounds.height = bd.height;
    return this.localBounds;
  }

  protected $draw(state: RenderState): void {
    const bd = this.bitmapData;
    if (!bd) return;
    const ctx = state.ctx;
    const previousSmoothing = ctx.imageSmoothingEnabled;
    ctx.imageSmoothingEnabled = this.smoothing;
    const ct = state.colorTransform;
    if (ct && !ct.isIdentity) {
      ctx.globalAlpha = state.alpha * ct.alphaMultiplier;
      ctx.drawImage(tint(bd, ct), 0, 0);
    } else {
      ctx.globalAlpha = state.alpha;
      ctx.drawImage(bd.source, 0, 0);
    }
    ctx.imageSmoothingEnabled = previousSmoothing;
  }

  private readonly localBounds = new Rectangle();
}

export class Stage extends DisplayObjectContainer {
  frameRate = 30;
  readonly stageWidth: number;
  readonly stageHeight: number;

  constructor(width: number, height: number) {
    super();
    this.stageWidth = width;
    this.stageHeight = height;
    stageInstance = this;
  }
}

export function currentStage(): Stage {
  if (!stageInstance) throw new Error("stage not created");
  return stageInstance;
}

let stageInstance: Stage | null = null;

interface BitmapFill {
  readonly bitmap: BitmapData;
  readonly repeat: boolean;
  readonly smooth: boolean;
  readonly patternMatrix: DOMMatrix | null;
  pattern: CanvasPattern | null;
  patternCtx: CanvasRenderingContext2D | null;
  patternVersion: number;
}

interface DrawCommand {
  readonly fill: BitmapFill | null;
  readonly rect: Rectangle;
}

interface Scratch {
  readonly canvas: HTMLCanvasElement;
  readonly ctx: CanvasRenderingContext2D;
}

/** Scratch canvases (with their contexts) for filter/mask/tint passes. */
const scratchPool: Scratch[] = [];
const MAX_POOLED_SCRATCH = 8;

// Shared temporaries for the render walk. Each is consumed before anything
// that could overwrite it runs, so no two live values ever share one.
const ancestorMatrix = new Matrix();
const boundsMatrix = new Matrix();
const maskMatrix = new Matrix();
const maskInverse = new Matrix();
const sizeBounds = new Rectangle();
const hitMatrix = new Matrix();

function takeScratch(width: number, height: number): Scratch {
  const scratch = scratchPool.pop() ?? createScratch();
  const canvas = scratch.canvas;
  if (canvas.width < width || canvas.height < height) {
    canvas.width = Math.max(canvas.width, width);
    canvas.height = Math.max(canvas.height, height);
  }
  const ctx = scratch.ctx;
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.globalAlpha = 1;
  ctx.globalCompositeOperation = "source-over";
  ctx.filter = "none";
  ctx.clearRect(0, 0, width, height);
  return scratch;
}

function releaseScratch(scratch: Scratch): void {
  if (scratchPool.length < MAX_POOLED_SCRATCH) scratchPool.push(scratch);
}

function createScratch(): Scratch {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("2d context unavailable");
  return { canvas, ctx };
}

/** out = outer concatenated with inner, matching ColorTransform.concat. */
function concatInto(
  outer: ColorTransform,
  inner: ColorTransform,
  out: ColorTransform,
): ColorTransform {
  out.redOffset = outer.redOffset + outer.redMultiplier * inner.redOffset;
  out.greenOffset =
    outer.greenOffset + outer.greenMultiplier * inner.greenOffset;
  out.blueOffset = outer.blueOffset + outer.blueMultiplier * inner.blueOffset;
  out.alphaOffset =
    outer.alphaOffset + outer.alphaMultiplier * inner.alphaOffset;
  out.redMultiplier = outer.redMultiplier * inner.redMultiplier;
  out.greenMultiplier = outer.greenMultiplier * inner.greenMultiplier;
  out.blueMultiplier = outer.blueMultiplier * inner.blueMultiplier;
  out.alphaMultiplier = outer.alphaMultiplier * inner.alphaMultiplier;
  return out;
}

/** Axis-aligned bounds of `local` mapped through `m`, without corner points. */
function transformBounds(
  local: Rectangle,
  m: Matrix,
  out: Rectangle,
): Rectangle {
  const left = local.x;
  const top = local.y;
  const right = local.right;
  const bottom = local.bottom;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (let corner = 0; corner < 4; corner++) {
    const cx = corner & 1 ? right : left;
    const cy = corner & 2 ? bottom : top;
    const px = m.a * cx + m.c * cy + m.tx;
    const py = m.b * cx + m.d * cy + m.ty;
    if (px < minX) minX = px;
    if (py < minY) minY = py;
    if (px > maxX) maxX = px;
    if (py > maxY) maxY = py;
  }
  out.setTo(minX, minY, maxX - minX, maxY - minY);
  return out;
}

function cssFilters(filters: readonly BitmapFilter[]): string {
  let out = filters[0].cssFilter;
  for (let i = 1; i < filters.length; i++) out += ` ${filters[i].cssFilter}`;
  return out;
}

interface TintEntry {
  readonly canvas: HTMLCanvasElement;
  readonly ctx: CanvasRenderingContext2D;
  redMultiplier: number;
  greenMultiplier: number;
  blueMultiplier: number;
  redOffset: number;
  greenOffset: number;
  blueOffset: number;
  version: number;
}

const tintCache = new WeakMap<BitmapData, TintEntry>();

/**
 * Applies a flash ColorTransform's colour channels to a bitmap.
 * Alpha is handled by the caller through globalAlpha, so only the RGB
 * multiply/offset pair is baked here.
 */
function tint(bd: BitmapData, ct: ColorTransform): HTMLCanvasElement {
  const cached = tintCache.get(bd);
  if (
    cached &&
    cached.version === bd.version &&
    cached.redMultiplier === ct.redMultiplier &&
    cached.greenMultiplier === ct.greenMultiplier &&
    cached.blueMultiplier === ct.blueMultiplier &&
    cached.redOffset === ct.redOffset &&
    cached.greenOffset === ct.greenOffset &&
    cached.blueOffset === ct.blueOffset
  ) {
    return cached.canvas;
  }

  const entry = cached ?? newTintEntry(bd);
  const ctx = entry.ctx;
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.globalCompositeOperation = "source-over";
  ctx.globalAlpha = 1;
  ctx.clearRect(0, 0, bd.width, bd.height);
  ctx.drawImage(bd.source, 0, 0);

  const rm = ct.redMultiplier;
  const gm = ct.greenMultiplier;
  const bm = ct.blueMultiplier;
  if (rm !== 1 || gm !== 1 || bm !== 1) {
    ctx.globalCompositeOperation = "multiply";
    ctx.fillStyle = `rgb(${Math.round(clamp01(rm) * 255)},${Math.round(clamp01(gm) * 255)},${Math.round(clamp01(bm) * 255)})`;
    ctx.fillRect(0, 0, bd.width, bd.height);
    // multiply leaves the untouched alpha, but zeroed channels must not bleed
    // outside the sprite silhouette, so re-clip to the source alpha.
    ctx.globalCompositeOperation = "destination-in";
    ctx.drawImage(bd.source, 0, 0);
  }
  const ro = ct.redOffset;
  const go = ct.greenOffset;
  const bo = ct.blueOffset;
  if (ro !== 0 || go !== 0 || bo !== 0) {
    const add = takeScratch(bd.width, bd.height);
    add.ctx.fillStyle = `rgb(${Math.max(0, Math.round(ro))},${Math.max(0, Math.round(go))},${Math.max(0, Math.round(bo))})`;
    add.ctx.fillRect(0, 0, bd.width, bd.height);
    add.ctx.globalCompositeOperation = "destination-in";
    add.ctx.drawImage(bd.source, 0, 0);
    ctx.globalCompositeOperation = "lighter";
    ctx.drawImage(
      add.canvas,
      0,
      0,
      bd.width,
      bd.height,
      0,
      0,
      bd.width,
      bd.height,
    );
    releaseScratch(add);
  }
  ctx.globalCompositeOperation = "source-over";

  entry.redMultiplier = rm;
  entry.greenMultiplier = gm;
  entry.blueMultiplier = bm;
  entry.redOffset = ro;
  entry.greenOffset = go;
  entry.blueOffset = bo;
  entry.version = bd.version;
  return entry.canvas;
}

function newTintEntry(bd: BitmapData): TintEntry {
  const canvas = document.createElement("canvas");
  canvas.width = bd.width;
  canvas.height = bd.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("2d context unavailable");
  const entry: TintEntry = {
    canvas,
    ctx,
    redMultiplier: 1,
    greenMultiplier: 1,
    blueMultiplier: 1,
    redOffset: 0,
    greenOffset: 0,
    blueOffset: 0,
    version: -1,
  };
  tintCache.set(bd, entry);
  return entry;
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}
